import { supabase } from "@/integrations/supabase/client";

/** Guidningsmeddelanden från systemadmin till en användare. */

export interface GuidanceMessage {
  id: string;
  recipient_user_id: string;
  project_id: string | null;
  sender_user_id: string;
  title: string;
  body: string;
  target_step: string | null;
  target_route: string | null;
  read_at: string | null;
  completed_at: string | null;
  dismissed_at: string | null;
  created_at: string;
}

/** Vanliga nästa steg att peka på – matchar appens egna vyer. */
export const GUIDANCE_TARGETS: { label: string; step: string; route: string }[] = [
  { label: "Kom igång-guiden", step: "start", route: "/guidning" },
  { label: "Skapa projekt", step: "project", route: "/uppdrag" },
  { label: "SKR-bedömning", step: "skr", route: "/skr-bedomning" },
  // AMP, arbetsberedning och samordning öppnas via steglösaren, som slår upp
  // (eller skapar) rätt dokument i projektet i stället för att visa en lista.
  { label: "Arbetsmiljöplan (AMP)", step: "amp", route: "/arbetsmiljoplan" },
  { label: "Riskbedömning", step: "risk", route: "/riskbedomning" },
  { label: "Arbetsberedning", step: "arbetsberedning", route: "/arbetsberedning" },
  { label: "Samordning", step: "samordning", route: "/samordning" },
  { label: "Mina dokument", step: "documents", route: "/dokument" },
];

/** Steg som alltid hör till ett projekt: id:t läggs på rutten när det finns. */
const PROJECT_SCOPED_ROUTES: Record<string, string> = {
  amp: "/amp",
  arbetsberedning: "/arbetsberedning",
  samordning: "/samordning",
};

/** Bygger en klickbar rutt: projektspecifika steg får med projektets id. */
export function resolveGuidanceRoute(message: {
  target_route: string | null;
  project_id?: string | null;
  target_step?: string | null;
}): string | null {
  if (!message.target_route) return null;
  const scoped = message.target_step ? PROJECT_SCOPED_ROUTES[message.target_step] : null;
  if (scoped) {
    return message.project_id ? `${scoped}/${message.project_id}` : message.target_route;
  }
  if (message.target_route === "/uppdrag" && message.project_id) {
    return `/uppdrag/${message.project_id}`;
  }
  return message.target_route;
}

export async function sendGuidance(input: {
  recipientUserId: string;
  senderUserId: string;
  projectId?: string | null;
  title: string;
  body: string;
  targetStep?: string | null;
  targetRoute?: string | null;
}) {
  const { error } = await supabase.from("admin_guidance_messages").insert({
    recipient_user_id: input.recipientUserId,
    sender_user_id: input.senderUserId,
    project_id: input.projectId ?? null,
    title: input.title,
    body: input.body,
    target_step: input.targetStep ?? null,
    target_route: input.targetRoute ?? null,
  });
  if (error) throw error;
}

export async function listGuidanceForRecipient(userId: string): Promise<GuidanceMessage[]> {
  const { data, error } = await supabase
    .from("admin_guidance_messages")
    .select("*")
    .eq("recipient_user_id", userId)
    .is("dismissed_at", null)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as GuidanceMessage[];
}

export async function listSentGuidance(limit = 50): Promise<GuidanceMessage[]> {
  const { data, error } = await supabase
    .from("admin_guidance_messages")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as GuidanceMessage[];
}

export async function markGuidanceRead(id: string) {
  const { error } = await supabase
    .from("admin_guidance_messages")
    .update({ read_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

export async function markGuidanceCompleted(id: string) {
  const { error } = await supabase
    .from("admin_guidance_messages")
    .update({ completed_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

export async function dismissGuidance(id: string) {
  const { error } = await supabase
    .from("admin_guidance_messages")
    .update({ dismissed_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}
